// components
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { Tooltip } from '@interstate/components/Tooltip';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import DealSummaryItem from '../common/DealSummaryItem';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents, kebabCase } from '@makemydeal/dr-common-utils';

// libraries
import { useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { DealHistory } from '@makemydeal/dr-dash-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import { useDealSummary } from '../../common/DealSummaryProvider';

// styles
import { detailedViewValueStyles } from '../utils/styles';

export type TotalOfPaymentsProps = {
    deal?: DealHistory;
    hasDelta?: boolean;
};

const TotalOfPayments = ({ deal, hasDelta = false }: TotalOfPaymentsProps) => {
    // The toggle and dealer state are live app-config gates, not per-deal data, so they must come
    // from the current redux state even when `deal` points this at a snapshot (e.g. compare
    // columns) — a saved offer's recorded featureToggles can predate this rollout and would
    // otherwise mask the row.
    const isCarsActDisclosuresApplicable = useSelector(dealerSelectors.isCarsActDisclosuresApplicable);
    const useDealRootSelector = createDealRootSelector(deal, 'TotalOfPayments');
    const totalOfPaymentsAndConsideration = useDealRootSelector(
        offerReduxSelectors.getTotalOfPaymentsAndConsideration,
        'totalOfPaymentsAndConsideration'
    );
    const { showDetailedView } = useDealSummary();
    const theme = useInterstateTheme();

    // Cash deals must never show this field/title (CARS Act label requirements); the underlying
    // selector already returns undefined for cash, since cash has no selected term.
    if (!isCarsActDisclosuresApplicable || totalOfPaymentsAndConsideration === undefined) return null;

    const label = constants.TOTAL_OF_PAYMENTS;
    const infoLabel = constants.TOTAL_OF_PAYMENTS_INFO_LABEL;
    // Derived from `label` (matching DealSummaryItem's own kebabCase(label) ids) so the icon and
    // tooltip testids stay in step with the row's label instead of drifting when it changes.
    const kebabLabel = kebabCase(label);

    return (
        <DealSummaryItem
            label={label}
            value={formatDollarsAndCents(totalOfPaymentsAndConsideration)}
            hasDelta={hasDelta}
            valueStyles={showDetailedView ? detailedViewValueStyles : {}}
            labelAction={
                // Tooltip wraps its child in a plain `<div>` (block-level), which would force a
                // line break in the label's plain-inline-flow layout above. This inline-block span
                // neutralizes that so the icon flows with the label text like an inline image instead
                // of dropping to its own line.
                <span style={{ display: 'inline-block' }}>
                    <Tooltip
                        position="right"
                        toolTipContent={constants.TOTAL_OF_PAYMENTS_TOOLTIP}
                        size="large"
                        data-testid={`${kebabLabel}-tooltip`}
                    >
                        <InformationCircleIcon
                            color={theme.tokens.BaseColorBlue500}
                            tabIndex={0}
                            role="button"
                            aria-label={infoLabel}
                            data-testid={`${kebabLabel}-information-circle-icon`}
                            style={{ marginLeft: '0.25rem', marginTop: '-0.125rem', verticalAlign: 'middle' }}
                        />
                    </Tooltip>
                </span>
            }
        />
    );
};

export default TotalOfPayments;
